import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { getRegistry } from "../../services/restAPI/RestApi";
import { useSelector } from "react-redux";

export type CaseColorStatus = "overdue" | "delayed" | "future" | "normal";

export type CaseColors = {
   background: string;
   dateBackground: string;
   dateText: string;
};

export type RegistryColorMap = Record<CaseColorStatus, CaseColors>;

const defaultColors: RegistryColorMap = {
   overdue: { background: "", dateBackground: "", dateText: "" },
   delayed: { background: "", dateBackground: "", dateText: "" },
   future: { background: "", dateBackground: "", dateText: "" },
   normal: { background: "", dateBackground: "", dateText: "" },
};
const statusList: CaseColorStatus[] = ["overdue", "delayed", "future", "normal"];

const RegistryColorsContext = createContext<RegistryColorMap>(defaultColors);

export const RegistryColorsProvider = ({ children }: { children: ReactNode }) => {
   const [colors, setColors] = useState<RegistryColorMap>(defaultColors);
   const authUser = useSelector(({ auth }) => auth?.authUser);

   useEffect(() => {
      let isCancelled = false;
      let retryTimeout: ReturnType<typeof setTimeout> | null = null;
      let retries = 0;
      const maxRetries = 10;
      const retryDelayMs = 500;
      const hasAuth = () => Boolean(localStorage.getItem("token") || authUser);

      const loadColors = async () => {
         const results = await Promise.all(
            statusList.map(async (status) => {
               const [bg, dateBg, dateText] = await Promise.all([
                  getRegistry(`${status}_case_background_color`),
                  getRegistry(`${status}_case_date_background_color`),
                  getRegistry(`${status}_case_date_text_color`),
               ]);

               return {
                  status,
                  colorSet: {
                     background: bg?.data?.default?.value ?? "",
                     dateBackground: dateBg?.data?.default?.value ?? "",
                     dateText: dateText?.data?.default?.value ?? "",
                  },
               };
            }),
         );

         const colorMap: RegistryColorMap = { ...defaultColors };
         for (const { status, colorSet } of results) {
            colorMap[status] = colorSet;
         }

         if (!isCancelled) {
            setColors(colorMap);
         }
      };

      const tryLoad = () => {
         if (!hasAuth()) {
            if (retries < maxRetries) {
               retries += 1;
               retryTimeout = setTimeout(tryLoad, retryDelayMs);
            }
            return;
         }

         loadColors().catch(() => {
            if (!isCancelled) setColors(defaultColors);
         });
      };

      tryLoad();

      return () => {
         isCancelled = true;
         if (retryTimeout) clearTimeout(retryTimeout);
      };
   }, [authUser]);

   return (
      <RegistryColorsContext.Provider value={colors}>{children}</RegistryColorsContext.Provider>
   );
};

export const useRegistryColorsContext = () => {
   const context = useContext(RegistryColorsContext);
   if (!context)
      throw new Error(
         "You are trying to use RegistryColors Context outside the RegistryColorsProvider",
      );
   return context;
};
